import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { onSnapshot, getDoc } from 'firebase/firestore';
import { auth } from '../firebase';
import { useAuth } from './AuthContext';
import {
  createCheckoutSession,
  createCustomerPortalSession,
} from '../utils/subscriptionService';
import { companyDetailsDocument, userProfileDocument } from '../lib/paths';

type SubscriptionStatus =
  | 'legacy'
  | 'pending'
  | 'trial'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'expired';

interface SubscriptionContextType {
  subscriptionStatus: SubscriptionStatus;
  trialDaysRemaining: number | null;
  isReadOnly: boolean;
  canCreateContent: boolean;
  loading: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: Date | null;
  hasCompanyDetails: boolean;
  redirectToCheckout: () => Promise<void>;
  redirectToPortal: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
  subscriptionStatus: 'legacy',
  trialDaysRemaining: null,
  isReadOnly: false,
  canCreateContent: true,
  loading: true,
  cancelAtPeriodEnd: false,
  currentPeriodEnd: null,
  hasCompanyDetails: false,
  redirectToCheckout: async () => {},
  redirectToPortal: async () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus>('legacy');
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(
    null
  );
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasCompanyDetails, setHasCompanyDetails] = useState(false);

  // Check if company details exist
  useEffect(() => {
    if (!user) return;

    const checkCompanyDetails = async () => {
      try {
        const companyDoc = await getDoc(companyDetailsDocument(user.uid));
        setHasCompanyDetails(companyDoc.exists());
      } catch {
        setHasCompanyDetails(false);
      }
    };

    checkCompanyDetails();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      userProfileDocument(user.uid),
      async snapshot => {
        const data = snapshot.data();

        if (!data || !data.subscriptionStatus) {
          // Legacy user — no subscription fields
          setSubscriptionStatus('legacy');
          setTrialDaysRemaining(null);
          setCancelAtPeriodEnd(false);
          setCurrentPeriodEnd(null);
          setLoading(false);
          return;
        }

        const status = data.subscriptionStatus as string;

        // Calculate trial days remaining
        if (status === 'trial' && data.trialEndsAt) {
          const trialEnd = data.trialEndsAt.toDate();
          const now = new Date();
          const daysLeft = Math.max(
            0,
            Math.ceil(
              (trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
            )
          );

          if (daysLeft <= 0) {
            // Trial expired but backend hasn't caught up yet
            setSubscriptionStatus('expired');
            setTrialDaysRemaining(0);
          } else {
            setSubscriptionStatus('trial');
            setTrialDaysRemaining(daysLeft);
          }
        } else {
          setSubscriptionStatus(status as SubscriptionStatus);
          setTrialDaysRemaining(null);
        }

        setCancelAtPeriodEnd(data.cancelAtPeriodEnd || false);
        setCurrentPeriodEnd(
          data.currentPeriodEnd ? data.currentPeriodEnd.toDate() : null
        );

        // Force token refresh to pick up new custom claims
        try {
          await auth.currentUser?.getIdToken(true);
        } catch {
          // Token refresh failed, claims will update on next natural refresh
        }

        setLoading(false);
      },
      () => {
        // Error handler — treat as legacy to not block user
        setSubscriptionStatus('legacy');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const isReadOnly =
    subscriptionStatus === 'expired' ||
    subscriptionStatus === 'canceled' ||
    subscriptionStatus === 'pending';

  const canCreateContent = !isReadOnly;

  const redirectToCheckout = useCallback(async () => {
    const { url } = await createCheckoutSession();
    window.location.href = url;
  }, []);

  const redirectToPortal = useCallback(async () => {
    const { url } = await createCustomerPortalSession();
    window.location.href = url;
  }, []);

  // Re-check company details when subscription status changes (e.g., after saving in onboarding)
  const refreshCompanyDetails = useCallback(async () => {
    if (!user) return;
    try {
      const companyDoc = await getDoc(companyDetailsDocument(user.uid));
      setHasCompanyDetails(companyDoc.exists());
    } catch {
      // ignore
    }
  }, [user]);

  // Expose refreshCompanyDetails by listening to subscription status changes
  useEffect(() => {
    if (subscriptionStatus === 'pending') {
      refreshCompanyDetails();
    }
  }, [subscriptionStatus, refreshCompanyDetails]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptionStatus,
        trialDaysRemaining,
        isReadOnly,
        canCreateContent,
        loading,
        cancelAtPeriodEnd,
        currentPeriodEnd,
        hasCompanyDetails,
        redirectToCheckout,
        redirectToPortal,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
